import React, { useState } from 'react';
import './ShipPurchaseDisplay.css';
import { formatNumber } from '../../utils/formatNumber';
import commandShipIcon from '../../assets/ships/command-ship.png';
import haulerShipIcon from '../../assets/ships/hauler-ship.png';
import miningFrigateIcon from '../../assets/ships/mining-frigate.png';
import freighterIcon from '../../assets/ships/freighter.png';
import bargeIcon from '../../assets/ships/barge.png';
import InfoPopover from '../InfoPopover';

export interface ShipPurchaseDisplayProps {
    name: string;
    image: string;
    cost: number;
    description: string;
    specificationDescription?: string;
    specificationsHeader?: string;
    specificationNote?: string;
    baseStats: {
        mining: number;
        cargo: number;
        speed: number;
        warpSpeed?: number;
        unloadSpeed?: number;
        fleetBonus?: number;
    };
    onPurchase: () => void;
    disabled?: boolean;
    playerOre?: number;
}

// Helper to map ship type or name to icon
function getShipIcon(name: string, fallback: string) {
    if (name.toLowerCase().includes('command')) return commandShipIcon;
    if (name.toLowerCase().includes('hauler')) return haulerShipIcon;
    if (name.toLowerCase().includes('frigate')) return miningFrigateIcon;
    if (name.toLowerCase().includes('freighter')) return freighterIcon;
    if (name.toLowerCase().includes('barge')) return bargeIcon;
    return fallback;
}

const ShipPurchaseDisplay: React.FC<ShipPurchaseDisplayProps> = ({
    name,
    image,
    cost,
    description,
    specificationDescription,
    specificationsHeader,
    specificationNote,
    baseStats,
    onPurchase,
    disabled,
    playerOre // <-- get from props
}) => {
    const [showInfo, setShowInfo] = useState(false);
    const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
    // Use the mapped icon if available
    const iconSrc = getShipIcon(name, image);
    // Determine border color and highlight class for affordability (like upgrade display)
    let highlightClass = '';
    const canAfford = !disabled && typeof playerOre === 'number' && playerOre >= cost;
    if (canAfford) {
        highlightClass = ' highlight-buyable';
    } else {
        highlightClass = ' highlight-unaffordable';
    }

    return (
        <div
            className={`ship-purchase-row upgrade-symbol-container${highlightClass}`}
            style={{
                position: 'relative',
                opacity: disabled ? 0.6 : 1,
                minWidth: 90,
                maxWidth: 90,
                width: 90, height: '8.5rem',
                margin: '0.5rem',
                borderRadius: 8,
                background: 'var(--color-bg-tertiary, #232a3a)',
                boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                padding: '0.25rem 0.25rem',
            }}
            onClick={() => { if (!disabled) onPurchase(); }}
            tabIndex={0}
            role="button"
            aria-disabled={disabled}
        >
            {/* Info button in top right, like upgrade display */}
            <button
                className="ship-purchase-info-btn"
                type="button"
                onClick={e => {
                    e.stopPropagation();
                    setPopoverAnchor(e.currentTarget);
                    setShowInfo(true);
                }}
                aria-label="Ship info"
                style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    background: 'var(--color-bg-secondary, #1a2638)',
                    border: '1px solid var(--color-border-light, #334466)',
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 3,
                    padding: 0,
                    outline: 'none',
                }}      >
                <span style={{ fontSize: '0.95em', color: '#2196f3', fontWeight: 'bold', lineHeight: 1 }}>i</span>
            </button>

            {/* Title container with fixed height for alignment */}
            <div style={{ height: 40, display: 'flex', alignItems: 'center', marginBottom: '0.3rem', width: '100%' }}>
                <div className="upgrade-bonus" style={{
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    textAlign: 'center',
                    whiteSpace: 'normal',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '100%',
                    lineHeight: 1.15
                }}>{name}</div>
            </div>            {/* Icon in a circle, centered, same as upgrade-symbol */}
            <div className="upgrade-symbol" style={{ position: 'relative', width: 50, height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.3rem' }}>
                {/* Import the getShipIconScale function for consistent ship sizing */}
                {(() => {
                    // Scale icon based on ship type
                    const getIconScale = () => {
                        const type = name.toLowerCase();
                        
                        if (type.includes('freighter')) {
                            return 3.0; // Freighters are largest
                        } else if (type.includes('hauler')) {
                            return 1.75; // Haulers are large
                        } else if (type.includes('command')) {
                            return 1; // Command ships are medium-large
                        } else if (type.includes('mining') || type.includes('frigate')) {
                            return 0.75; // Mining ships are smaller
                        }
                        
                        // Default scale
                        return 1;
                    };

                    const iconScale = getIconScale();
                    const imgSize = Math.min(85 * iconScale, 45); // Cap at 45px to fit in parent container
                    
                    return (
                        <img 
                            src={iconSrc} 
                            alt={name} 
                            style={{ 
                                width: imgSize, 
                                height: imgSize, 
                                objectFit: 'contain', 
                                zIndex: 1 
                            }} 
                            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} 
                        />
                    );
                })()}
            </div>      {/* Cost, no label, just value */}
            <div className="upgrade-cost" style={{ fontWeight: 'bold', color: 'var(--color-accent-blue, #2196f3)', fontSize: '0.9rem', marginBottom: '0.2rem', textAlign: 'center', width: '100%' }}>
                {formatNumber(cost)} Ore</div>
            {showInfo && (
                <InfoPopover
                    anchorEl={popoverAnchor}
                    title={name}
                    onClose={() => setShowInfo(false)}
                    placement="bottom" // Match the placement used in MiningZoneSelector
                >          <div style={{ marginBottom: 10, fontSize: '0.97em', opacity: 0.85 }}>{description}</div>
                    {specificationDescription && (
                        <div style={{ marginBottom: 10, fontSize: '0.97em', opacity: 0.85 }}>{specificationDescription}</div>
                    )}
                    <div style={{ fontSize: '0.97em', fontWeight: 'bold', borderBottom: '1px solid var(--color-border-light, #334466)', paddingBottom: '4px', marginBottom: '4px' }}>
                        {specificationsHeader || "Ship Specifications"}
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.97em' }}>
                        <tbody>                            {/* Mining Rate for all ship types - show 0 for non-mining ships */}
                            <tr>
                                <td style={{ fontWeight: 600, padding: '4px 8px' }}>Mining Rate</td>
                                <td style={{ textAlign: 'right', padding: '4px 8px', color: baseStats.mining > 0 ? 'var(--color-success, #4caf50)' : 'var(--color-accent-blue, #2196f3)' }}>{baseStats.mining} ore/s</td>
                            </tr>

                            {/* Cargo Capacity for all ship types */}
                            <tr>
                                <td style={{ fontWeight: 600, padding: '4px 8px' }}>Cargo Capacity</td>
                                <td style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--color-accent-blue, #2196f3)' }}>{baseStats.cargo} ore</td>
                            </tr>

                            {/* Travel Speed for all ship types */}
                            <tr>
                                <td style={{ fontWeight: 600, padding: '4px 8px' }}>Travel Speed</td>
                                <td style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--color-accent-blue, #2196f3)' }}>{baseStats.speed} au/s</td>
                            </tr>

                            {/* Unload Rate for all ship types */}
                            <tr>
                                <td style={{ fontWeight: 600, padding: '4px 8px' }}>Unload Rate</td>
                                <td style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--color-accent-blue, #2196f3)' }}>{baseStats.unloadSpeed || 0} ore/s</td>
                            </tr>

                            {/* Fleet Bonus only for command ships */}
                            {name.toLowerCase().includes('command') && baseStats.fleetBonus && (
                                <tr>
                                    <td style={{ fontWeight: 600, padding: '4px 8px' }}>Fleet Bonus</td>
                                    <td style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--color-success, #4caf50)' }}>+{baseStats.fleetBonus}%</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    <div style={{ fontSize: '0.8em', marginTop: '8px', textAlign: 'center', opacity: '0.7' }}>
                        {specificationNote}
                    </div>
                </InfoPopover>
            )}
        </div>
    );
};

// ShipPurchasePanel container for purchase cards
export const ShipPurchasePanel: React.FC<{
    ships: Array<React.ComponentProps<typeof ShipPurchaseDisplay>>;
    header?: string;
}> = ({ ships, header = 'Purchase Ships' }) => {
    return (
        <div
            className="ship-category-section size-small"
            style={{
                margin: '1.5rem auto',
                maxWidth: 600,
                background: 'var(--color-bg-tertiary, #232a3a)',
                borderRadius: 8,
                border: '1.5px solid var(--color-border-light, #334466)',
                boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                padding: '0.5rem 0 1.5rem 0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >      <div className="category-header" style={{ width: '100%', padding: '0.5rem 0', borderBottom: '1.5px solid var(--color-border-light, #334466)', marginBottom: '1rem', textAlign: 'center', background: 'var(--color-bg-secondary, #1a2638)', borderTopLeftRadius: 8, borderTopRightRadius: 8 }}>
                <h3 style={{ margin: 0, color: 'var(--color-success, #4caf50)', fontWeight: 700, fontSize: '1rem', letterSpacing: 1 }}>{header}</h3>
            </div>
            <div className="ship-purchase-list" style={{ display: 'flex', flexDirection: 'row', gap: '0.5rem', justifyContent: 'center', alignItems: 'flex-end', flexWrap: 'wrap', width: '100%' }}>
                {ships.map((props, i) => (
                    <ShipPurchaseDisplay key={props.name + i} {...props} />
                ))}
            </div>
        </div>
    );
};

export default ShipPurchaseDisplay;
